"use client";
import { Button } from "@/components/liquid-glass-button";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
import { paidFeaturesLive } from "@/lib/tier-blocking";
import { createClient } from "@/utils/supabase/client";
import { useLenis } from "lenis/react";
import { Equal, X } from "lucide-react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import React, { useEffect } from "react";

type DropdownKey = "product" | "use-cases" | "knowledge";

interface NaverProps {
  logo?: {
    src?: string;
    svg?: React.ReactNode;
    alt?: string;
  };
  logoTitle?: string;
  menuItems: Array<{
    name: string;
    href: string;
    current?: boolean;
    dropdown?: DropdownKey;
    accent?: "sustainability";
  }>;
  dropdowns?: Partial<Record<DropdownKey, React.ReactNode>>;
}

export const Naver = ({
  logo,
  logoTitle,
  menuItems,
  dropdowns,
}: NaverProps) => {
  const [menuState, setMenuState] = React.useState(false);
  const [isScrolled, setIsScrolled] = React.useState(false);
  const [isLoggedIn, setIsLoggedIn] = React.useState<boolean | null>(null);
  const lenis = useLenis();
  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    const supabase = createClient();
    let cancelled = false;
    supabase.auth.getSession().then(({ data }) => {
      if (!cancelled) setIsLoggedIn(!!data.session);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsLoggedIn(!!session);
    });
    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMenuState(false);
  }, [pathname]);

  useEffect(() => {
    if (!menuState) return;
    const root = document.documentElement;
    const previousOverflow = root.style.overflow;
    root.style.overflow = "hidden";
    lenis?.stop();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMenuState(false);
    };
    const desktopQuery = window.matchMedia("(min-width: 64rem)");
    const handleDesktop = (event: MediaQueryListEvent) => {
      if (event.matches) setMenuState(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    desktopQuery.addEventListener("change", handleDesktop);
    return () => {
      root.style.overflow = previousOverflow;
      lenis?.start();
      window.removeEventListener("keydown", handleKeyDown);
      desktopQuery.removeEventListener("change", handleDesktop);
    };
  }, [menuState, lenis]);
  return (
    <header>
      <nav
        data-state={menuState && "active"}
        className="fixed left-0 w-full z-100 px-2"
      >
        <div
          className={cn(
            "mx-auto mt-4 max-w-[90rem] px-2 transition-all duration-300 sm:px-6 lg:px-12",
            isScrolled &&
              "bg-background/50 max-w-7xl rounded-2xl border backdrop-blur-sm lg:px-5",
          )}
        >
          <div className="relative flex flex-wrap items-center justify-between gap-6 lg:gap-0 py-2">
            <div className="flex w-full justify-between lg:w-auto">
              {(logo || logoTitle) && (
                <Link
                  href="/"
                  aria-label="home"
                  className="flex gap-2 items-center"
                >
                  {logo?.svg ? (
                    logo.svg
                  ) : logo?.src ? (
                    <img
                      src={logo.src}
                      alt={logo.alt || "Logo"}
                      className="w-6 h-6"
                    />
                  ) : null}
                  {logoTitle && (
                    <p className="font-medium text-xl tracking-tighter">
                      {logoTitle}
                    </p>
                  )}
                </Link>
              )}

              <button
                type="button"
                onClick={() => setMenuState(!menuState)}
                aria-label={menuState == true ? "Close Menu" : "Open Menu"}
                aria-expanded={menuState}
                aria-controls="mobile-nav-menu"
                className="relative z-20 -m-2.5 block cursor-pointer p-2.5 sm:-mr-4 lg:hidden"
              >
                <Equal className="in-data-[state=active]:rotate-180 in-data-[state=active]:scale-0 in-data-[state=active]:opacity-0 m-auto size-6 duration-200" />
                <X className="in-data-[state=active]:rotate-0 in-data-[state=active]:scale-100 in-data-[state=active]:opacity-100 absolute inset-0 m-auto size-6 -rotate-180 scale-0 opacity-0 duration-200" />
              </button>
            </div>

            <div className="absolute inset-0 m-auto hidden size-fit lg:block">
              <NavigationMenu>
                <NavigationMenuList className="gap-1">
                  {menuItems.map((item, index) => {
                    const hasDropdown =
                      !!item.dropdown && !!dropdowns?.[item.dropdown];
                    if (hasDropdown) {
                      return (
                        <NavigationMenuItem key={index}>
                          <NavigationMenuTrigger className="h-9 bg-transparent px-3 text-sm font-normal text-neutral-900 hover:bg-transparent hover:text-neutral-600 focus:bg-transparent data-[state=open]:bg-transparent data-[state=open]:text-neutral-600 data-[state=open]:hover:bg-transparent">
                            {item.name}
                          </NavigationMenuTrigger>
                          <NavigationMenuContent className="p-4">
                            {dropdowns![item.dropdown!]}
                          </NavigationMenuContent>
                        </NavigationMenuItem>
                      );
                    }
                    return (
                      <NavigationMenuItem key={index}>
                        <NavigationMenuLink
                          asChild
                          className={cn(
                            "h-9 rounded-md bg-transparent px-3 py-2 text-sm font-normal text-neutral-900 hover:bg-transparent hover:text-neutral-600 focus:bg-transparent",
                            item.accent === "sustainability" &&
                              "text-emerald-700 hover:text-emerald-600 focus:text-emerald-700",
                          )}
                        >
                          <Link href={item.href}>{item.name}</Link>
                        </NavigationMenuLink>
                      </NavigationMenuItem>
                    );
                  })}
                </NavigationMenuList>
              </NavigationMenu>
            </div>

            <div
              id="mobile-nav-menu"
              data-lenis-prevent
              className="bg-background in-data-[state=active]:block lg:in-data-[state=active]:flex mb-6 hidden max-h-[calc(100dvh-7rem)] w-full flex-wrap items-center justify-end space-y-8 overflow-y-auto overscroll-contain rounded-3xl border p-6 shadow-2xl shadow-zinc-300/20 md:flex-nowrap lg:m-0 lg:flex lg:max-h-none lg:w-fit lg:gap-6 lg:space-y-0 lg:overflow-visible lg:border-transparent lg:bg-transparent lg:p-0 lg:shadow-none dark:shadow-none dark:lg:bg-transparent"
            >
              <div className="lg:hidden">
                <ul className="space-y-2 text-base">
                  {menuItems.map((item, index) => (
                    <li key={index}>
                      <Link
                        href={item.href}
                        onClick={() => setMenuState(false)}
                        className={cn(
                          "flex min-h-10 items-center text-neutral-900 duration-150 hover:text-neutral-600",
                          item.accent === "sustainability" &&
                            "text-emerald-700 hover:text-emerald-600",
                        )}
                      >
                        <span>{item.name}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex w-full flex-col space-y-3 sm:flex-row sm:gap-2 sm:space-y-0 md:w-fit">
                {isLoggedIn ? (
                  <Button
                    size="sm"
                    className={cn(isScrolled && "lg:hidden")}
                    onClick={() => router.push("/dashboard")}
                  >
                    Dashboard
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      className={cn(isScrolled && "lg:hidden")}
                      onClick={() => router.push("/auth/signin")}
                    >
                      Login
                    </Button>
                    <Button
                      size="sm"
                      className={cn(isScrolled && "lg:hidden")}
                      onClick={() => router.push("/auth/signin")}
                    >
                      Sign In
                    </Button>
                  </>
                )}
                <Button
                  asChild
                  size="sm"
                  className={cn(isScrolled ? "lg:inline-flex" : "hidden")}
                >
                  <Link href="/auth/signin">
                    <span>
                      {paidFeaturesLive()
                        ? "Get Started"
                        : "Join now for a free handyout"}
                    </span>
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
};
